import { hardNavigation } from "../../utils/hard-navigation.util";
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { NgbPopoverConfig } from "@ng-bootstrap/ng-bootstrap";
import { cloneDeep, findIndex, isNumber } from "lodash";
import { Subject, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";

import { DataService } from "src/app/core/core-services/services/dataservices/data.service";
import { DIALOG_WIDTHS, DialogService } from "src/app/core/core-services/services/dialog.service";
import { EventService } from "src/app/core/core-services/services/event.service";
import { ExtensionService } from "src/app/core/core-services/services/extension.service";
import { ListService } from "src/app/core/core-services/services/list.service";
import { NotificationService } from "src/app/core/core-services/services/notification.service";
import { ScrollService } from "src/app/core/core-services/services/scroll.service";
import { ShortcutService } from "src/app/core/core-services/services/shortcut.service";
import { TinyMceConfigService } from "src/app/core/core-services/services/tiny-mce-config.service";
import { Store } from "@ngrx/store";
import { selectPlanFeatures } from "src/app/core/store/plan/plan.selectors";
import { take, filter } from "rxjs/operators";

import { ApplicationConstants } from "src/app/core/core-services/constants/application.constants";

import { MetaService } from "src/app/core/core-services/services/meta.service";
import { FlyCutShareComponent } from "src/app/vengreso/share/fly-cut-share/fly-cut-share.component";
import {
  PlansConstants,
  PlansRestrictions,
  RewardsConstants,
  getPlanId,
  getRewards,
} from "../../core/core-services/constants/plans.constants";
import { WarningDialogComponent } from "../../core/core-services/warning-dialog/warning-dialog.component";
import { EmulatorPreviewComponent } from "./actions/emulator-preview/emulator-preview.component";
import { StatisticsComponent } from "./actions/statistics/statistics.component";
import { AddShortcutComponent } from "./add-shortcut/add-shortcut.component";
import { ChangeShortcutCategoryComponent } from "./change-shortcut-category/change-shortcut-category.component";
import { DuplicateShortcutComponent } from "./duplicate-shortcut/duplicate-shortcut.component";
import { VersionHistoryComponent } from "./version-history/version-history.component";
import { SharedDataService } from "src/app/core/core-services/services/shared-data.service";
import { TestFlycutComponent } from "./test-flycut/test-flycut.component";
import { clearTinyMCEContent } from "src/app/utils/getSortcutContent";
import { FlyCut } from "src/app/core/models/flymsg.model";

const placeholders = {
  textArea:
    "Test your FlyCut here by typing it in now. Remember will not display any rich text like bold, underline, words that are hyperlinked, images, etc. Switch to Rich Text Preview to test those elements.",
  testingRichEditor: "Test your FlyCut here by typing it in now.",
};

import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { NgbPopoverModule } from "@ng-bootstrap/ng-bootstrap";
import { EditorModule } from "@tinymce/tinymce-angular";
import { InfiniteScrollModule } from "ngx-infinite-scroll";
import { HighlighterPipe } from "src/app/core/core-services/highlighter/highlighter.pipe";
import { DownloadBannerComponent } from "src/app/download-banner/download-banner.component";
import { SvgIconsModule } from "src/app/module/svg.module";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import {
  VngCheckboxComponent,
  VngIconComponent,
  VngButtonComponent,
  VngTooltipDirective,
  VngDividerComponent,
  VngExpansionPanelComponent,
  VngSelectOption,
} from "@vengreso/angular-components";
import { FlymsgRowHeaderComponent } from "src/app/vengreso/shared/flymsg-row/flymsg-row-header/flymsg-row-header.component";
import { FlymsgRowEditorComponent } from "src/app/vengreso/shared/flymsg-row/flymsg-row-editor/flymsg-row-editor.component";
import { EmptyStateComponent } from "src/app/components/empty-state/empty-state.component";

// C3-4: this component is standalone, so its own `imports:` array is private —
// removing the now-fully-migrated Mat*Module entries here carries none of the
// shared-module merge-conflict risk the §4 only-add rule guards against
// (nothing else compiles against ShortcutsComponent's decorator).
@Component({
    selector: "app-shortcuts",
    templateUrl: "./shortcuts.component.html",
    styleUrls: ["./shortcuts.component.scss"],
    imports: [
        CommonModule,
        InfiniteScrollModule,
        DownloadBannerComponent,
        VngCheckboxComponent,
        VngIconComponent,
        NgbPopoverModule,
        VngButtonComponent,
        VngDividerComponent,
        VngExpansionPanelComponent,
        VngTooltipDirective,
        FormsModule,
        EditorModule,
        HighlighterPipe,
        SvgIconsModule,
        NgxSkeletonLoaderModule,
        FlymsgRowHeaderComponent,
        FlymsgRowEditorComponent,
        EmptyStateComponent,
    ]
})
export class ShortcutsComponent implements OnInit, OnDestroy {
  @Input() searchShortcuts: FlyCut[];
  @Input() fromSearch: any;
  @Input() upScroll: boolean;
  @Input() searchValue: string;
  @Output("detectUpdates") detectUpdates: EventEmitter<any> = new EventEmitter();
  plc = placeholders;
  shortcuts: FlyCut[];
  categoryId: string;
  lv1CategoryId: string;
  lv2CategoryId: string;
  shortcutsCopy: FlyCut[];
  testingRichEditor: string;
  textArea: string;
  testElement: string;
  editorConfig: any = null; // Initialize as null to prevent undefined errors
  isSaving: boolean = false;
  currentIndex: number;
  panelOpenState = {};
  panelExpandState = {};
  subscriptions = new Subscription();
  modelChanged = new Subject<string>();
  editorApiKey: string;
  editorManager: any;
  scrollDisabled = true;
  searchText = "";
  allShortCutChecked: boolean = false;
  noShortcutChecked: boolean = false;
  initialLoading = true;

  scrollerTimeout;
  flycutIndexToBeDeleted: number;

  // R5: the row "Action" mat-select was a fake actions-menu (no ngModel ever
  // bound; each option just fired a side effect on click) — data-driven
  // vng-select options + a single (selectionChange) dispatcher preserve that.
  rowActionOptions: VngSelectOption[] = [
    { value: "preview", label: "Preview", data: { icon: "/assets/images/preview_black.png" } },
    { value: "word_count", label: "Word Count", data: { icon: "/assets/images/state_black.png" } },
    { value: "version_history", label: "Version History", data: { icon: "/assets/images/clock_black.png" } },
    { value: "share", label: "Share", data: { icon: "/assets/images/share_black.png" } },
  ];

  onRowAction(shortcut: FlyCut, action: string) {
    switch (action) {
      case "preview":
        this.openEmulatorPreview(shortcut);
        break;
      case "word_count":
        this.openStat(shortcut.id);
        break;
      case "version_history":
        this.openVersionHistory(shortcut, undefined);
        break;
      case "share":
        this.openFlyCutShare(shortcut);
        break;
    }
  }

  constructor(
    config: NgbPopoverConfig,
    public dialog: DialogService,
    private readonly route: ActivatedRoute,
    public listService: ListService,
    private readonly dataService: DataService,
    private readonly eventService: EventService,
    public scrollService: ScrollService,
    private readonly shortcutService: ShortcutService,
    private readonly notification: NotificationService,
    private readonly extensionService: ExtensionService,
    private readonly router: Router,
    private readonly metaService: MetaService,
    private readonly sharedData: SharedDataService,
    private readonly el: ElementRef,
    private readonly tinyMceConfigService: TinyMceConfigService,
    private readonly store: Store
  ) {
    config.triggers = "hover";
    config.container = "body";

    let expand = this.sharedData.getAttribute("expand");
    if (expand) {
      this.sharedData.deleteAttribute("expand");
      this.panelOpenState[expand] = true;
      this.panelExpandState[expand] = true;
    }

    this.modelChanged.pipe(debounceTime(1000)).subscribe(() => {
      if (isNumber(this.currentIndex)) {
        let isChangeDetected = false;

        if (this.isContentChanged("html")) {
          isChangeDetected = true;
          this.shortcuts[this.currentIndex].first_line = this.shortcutService.getFirstLine(
            this.editorManager.get("shortcut" + this.shortcuts[this.currentIndex].id)
          );
        }

        if (this.isContentChanged("title") || this.isContentChanged("shortcut")) {
          isChangeDetected = true;
        }

        if (isChangeDetected) {
          this.setContentToCopy("html");
          this.setContentToCopy("title");
          this.setContentToCopy("shortcut");
          this.saveShortcut(this.shortcuts[this.currentIndex], "shortcut" + this.shortcuts[this.currentIndex].id);
        } else {
          const index = findIndex(this.shortcuts, { id: this.shortcuts[this.currentIndex].id });
          this.shortcuts[index].errors = [];
        }
      }
    });
  }
  onEditorInput(event: any) {
    this.testingRichEditor = event.target.innerHTML;
  }
  onChangeEditor(editor: "text" | "edit") {
    this.testElement = editor;
    this.showPlaceholder();
    this.textArea = placeholders.textArea;
    this.testingRichEditor = placeholders.testingRichEditor;
  }
  ngOnChanges() {
    this.shortcuts = this.searchShortcuts;
    if (this.fromSearch) {
      this.searchText = this.searchValue;
    }
  }
  placeholder = true;
  hidePlaceholder() {
    this.placeholder = false;
  }
  showPlaceholder() {
    this.placeholder = this.testingRichEditor === "" || this.testingRichEditor == placeholders.testingRichEditor;
  }
  async initialise() {
    this.testElement = "text";
    // tslint:disable-next-line:max-line-length
    this.textArea = placeholders.textArea;
    this.testingRichEditor = placeholders.testingRichEditor;

    this.scrollDisabled = false;
    this.editorApiKey = ApplicationConstants.TINYMCE_API_KEY;

    this.store.select(selectPlanFeatures).pipe(
      filter(features => !!features),
      take(1)
    ).subscribe(features => {
      const baseConfig = this.shortcutService.getEditorConfig();
      const planId = getPlanId();

      this.editorConfig = this.tinyMceConfigService.getPlanBasedConfig(
        features || {} as any,
        baseConfig,
        this,
        planId,
        (type, error, showTheStarter) => {
          this.dialog.openDialogComponent(
            WarningDialogComponent,
            {
              self: this,
              type: 'editor',
              showTheStarter: showTheStarter,
              error: error,
              isCustomMessage: true
            },
            "800px"
          );
        }
      );
      this.editorConfig.placeholder = this.testingRichEditor;
    });

    this.subscriptions.add(
      this.route.data.subscribe(
        (data) => {
          this.initialLoading = true;
          this.categoryId = this.route.snapshot.paramMap.get("categoryId");
          this.lv1CategoryId = this.route.snapshot.paramMap.get("lv1CategoryId");
          this.lv2CategoryId = this.route.snapshot.paramMap.get("lv2CategoryId");

          if ((data.shortcutList && data.shortcutList.result) || this.fromSearch) {
            let url = `v1/user/shortcut?order_by=shortcut,asc`;

            if (this.categoryId !== "all") {
              url += `&category_id=${this.categoryId}`;
            }
            if (this.lv1CategoryId) {
              url += `&sub_categories_lv1_id=${this.lv1CategoryId}`;
            }
            if (this.lv2CategoryId) {
              url += `&sub_categories_lv2_id=${this.lv2CategoryId}`;
            }

            const config = {
              self: this,
              listName: "shortcuts",
              url: url + "&",
              callback: () => {
                this.shortcuts = this.shortcuts.map((shortcut) => {
                  return {
                    ...shortcut,
                    mappedId: `id${shortcut.id}`,
                  };
                });
                this.shortcutsCopy = cloneDeep(this.shortcuts);
                setTimeout(() => {
                  this.initialLoading = false;
                }, 10);
              },
            };

            this.fromSearch == true
              ? this.listService.init(config, this.searchShortcuts)
              : this.listService.init(config, data.shortcutList.result);
          }
        },
        () => {
          this.initialLoading = false;
        }
      )
    );

    this.subscriptions.add(
      this.route.queryParams.subscribe((params) => {
        if (params.add === "true") {
          this.addNewShortcut();
        }
        if (params.shortcutId) {
          this.dataService.get({ url: `v1/user/shortcut/${params.shortcutId}` }).subscribe({
            next: (response: any) => {
              let modal = this.dialog.openDialogComponent(
                AddShortcutComponent,
                { self: this, action: "edit", shortcut: response.result },
                "800px"
              );
              modal.afterClosed().subscribe((res) => {
                if (res?.success && res?.action == "edit") {
                  this.shortcuts.unshift(res.result);
                }
              });
            },
            error: (err) => { },
          });
        }
      })
    );

    this.subscriptions.add(
      this.eventService.getAddShortcutListener().subscribe((data) => {
        if (data) {
          this.addNewShortcut();
        }
      })
    );
  }

  scrollToShortcutToBeEdited(shortcutId: string) {
    const index = this.shortcuts.findIndex((shortcut) => {
      return shortcut.id === shortcutId;
    });
    if (index !== -1) {
      this.scrollerTimeout = setTimeout(() => {
        const element = this.el.nativeElement.querySelector(`#id${shortcutId}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" }); // Use 'auto' for instant scrolling, Use 'smmooth' for smooth scrolling
        }
        this.clearScrollingEvents();
        return;
      }, 500);
    }
  }

  clearScrollingEvents() {
    clearTimeout(this.scrollerTimeout);
  }

  async ngOnInit() {
    this.initialise();
    this.metaService.addMetaTag(
      "FlyMSG View All FlyCuts Page",
      "FlyMSG can help you view all of your FlyCuts in one view all page, making it easier to navigate your templates."
    );
  }

  ngOnDestroy() {
    this.clearScrollingEvents();
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  reRender() {
    hardNavigation.reload();
  }

  setEditorManager(event: any) {
    this.editorManager = event.editor.editorManager;
  }

  isContentChanged(key: string | number) {
    const current = this.shortcuts[this.currentIndex][key];
    const copy = this.shortcutsCopy[this.currentIndex][key];
    if (!current) return false;
    // Compare normalized HTML to avoid false positives from TinyMCE reformatting on focus
    if (key === 'html') {
      return clearTinyMCEContent(copy || '') !== clearTinyMCEContent(current || '');
    }
    return copy !== current;
  }

  setContentToCopy(key: string | number) {
    this.shortcutsCopy[this.currentIndex][key] = this.shortcuts[this.currentIndex][key];
  }

  onChangeContent(index: number) {
    this.modelChanged.next(null);
    this.currentIndex = index;
  }

  saveShortcut(data: FlyCut, editorId: string) {
    const editor = this.editorManager.get(editorId);
    this.shortcutService.processMedia(
      editor,
      (uploads: string[]) => {
        const shortcut: any = this.dataService.toSave(data, ["title", "shortcut", "category_id", "first_line"]);
        shortcut.uploads = uploads;
        shortcut.html = clearTinyMCEContent(editor.getContent());
        shortcut.text = this.shortcutService.getInlineStyledHTML(shortcut.html);
        if (shortcut.title && shortcut.shortcut && shortcut.html) {
          this.shortcutUpdate(data, shortcut);
        }
      },
      data?.uploads
    );
  }

  isValidateShortcut(data: FlyCut) {
    return true; // Centralized validation handled via TinyMceConfigService
  }

  shortcutUpdate(data: FlyCut, shortcut: any, isCategoryUpdate = false) {
    if (!this.isValidateShortcut(data)) return;

    this.isSaving = true;
    this.dataService
      .put({
        url: "v1/user/shortcut/" + data.id,
        data: shortcut,
        isLoader: false,
      })
      .subscribe(
        (response: any) => {
          if (response) {
            this.isSaving = false;
            try {
              this.extensionService.sendUpdateMessage();
            } catch { }

            const index = findIndex(this.shortcuts, { id: data.id });

            //To propagate the changes to the dialog components incase of preview since fetching of shortcuts doesn't happen after update.
            this.shortcuts[index].shortcut = shortcut.shortcut;
            this.shortcuts[index].title = shortcut.title;
            this.shortcuts[index].text = shortcut.text;

            if (isCategoryUpdate) {
              // if in all shortcut or dashboard update the categories id in the frontend witout refresh
              const currentRoute = this.router.url;

              if (currentRoute.includes("shortcuts/all") || currentRoute.includes("/advanced-search-result")) {
                // Soft Update category without refresh
                this.shortcuts[index].category_id = response.result.category_id;
                this.shortcuts[index].sub_categories_lv1_id = response.result.sub_categories_lv1_id;
                this.shortcuts[index].sub_categories_lv2_id = response.result.sub_categories_lv2_id;

                const category_name = response.result.category_name;

                switch (shortcut.category_level) {
                  case 0:
                    this.shortcuts[index].category_name = category_name;
                    this.shortcuts[index].sub_categories_lv1_name = "";
                    this.shortcuts[index].sub_categories_lv2_name = "";
                    break;
                  case 1:
                    this.shortcuts[index].category_name = "";
                    this.shortcuts[index].sub_categories_lv1_name = category_name;
                    this.shortcuts[index].sub_categories_lv2_name = "";
                    break;
                  case 2:
                    this.shortcuts[index].category_name = "";
                    this.shortcuts[index].sub_categories_lv1_name = "";
                    this.shortcuts[index].sub_categories_lv2_name = category_name;
                    break;
                }
              } else {
                this.fetchShortcuts();
              }

              this.notification.toast("Category updated successfully!");
            }

            this.shortcuts[index].errors = [];
            if (!isCategoryUpdate) {
              if (isNaN(this.shortcuts[index].version_counts)) {
                this.shortcuts[index].version_counts = 1;
              } else {
                this.shortcuts[index].version_counts++;
              }
            }
          }
        },
        (err) => {
          if (err.status == 403) {
            const index = findIndex(this.shortcuts, { id: data.id });
            if (!this.shortcuts[index].hasOwnProperty("errors")) {
              this.shortcuts[index].errors = [];
            }
          }
          this.isSaving = false;
        }
      );
  }

  addNewShortcut() {
    let planId = getPlanId();
    let rewards = getRewards();
    if (!rewards.length && !rewards.includes(RewardsConstants.UNLIMITED_FLYCUTS)) {
      if (planId === "Free" && this.shortcuts.length >= PlansConstants.FREE.NUM_OF_FLYCUTS) {
        this.dialog.openDialogComponent(
          WarningDialogComponent,
          { self: this, type: "numOfFlycuts", showTheStarter: planId === "Free", error: "maxFree" },
          "800px"
        );
        return;
      }
      if (planId === "Starter" && this.shortcuts.length >= PlansConstants.STARTER.NUM_OF_FLYCUTS) {
        this.dialog.openDialogComponent(
          WarningDialogComponent,
          { self: this, type: "numOfFlycuts", showTheStarter: false, error: "maxStarter" },
          "800px"
        );
        return;
      }
    }

    let currentUrl = this.router.url || "shortcuts/all";
    let modal = this.dialog.openDialogComponent(AddShortcutComponent, { self: this, parentPage: currentUrl }, "800px");

    modal.afterClosed().subscribe((result) => {
      if (result?.success) {
        setTimeout(() => {
          this.fetchShortcuts();
        }, 1000);
      }
    });
  }

  fetchShortcuts() {
    this.route.data.subscribe((data) => {
      this.categoryId = this.route.snapshot.paramMap.get("categoryId");
      this.lv1CategoryId = this.route.snapshot.paramMap.get("lv1CategoryId");
      this.lv2CategoryId = this.route.snapshot.paramMap.get("lv2CategoryId");

      if ((data.shortcutList && data.shortcutList.result) || this.fromSearch) {
        let url = `v1/user/shortcut?order_by=shortcut,asc`;

        if (this.categoryId !== "all") {
          url += `&category_id=${this.categoryId}`;
        }
        if (this.lv1CategoryId) {
          url += `&sub_categories_lv1_id=${this.lv1CategoryId}`;
        }
        if (this.lv2CategoryId) {
          url += `&sub_categories_lv2_id=${this.lv2CategoryId}`;
        }

        this.dataService
          .get({
            url,
          })
          .subscribe((response: any) => {
            this.shortcuts = response?.result;
            this.shortcutsCopy = cloneDeep(this.shortcuts);
            // Keep listService.skip aligned with the refreshed list, otherwise
            // the next infinite-scroll fetch asks for the wrong page and
            // duplicates can appear.
            if (this.listService?.config) {
              this.listService.config.skip = this.shortcuts?.length || 0;
              this.listService.scrollDisabled = (this.shortcuts?.length || 0) < this.listService.config.limit;
            }
          });
      }
    });
  }
  openedPanel(id) {
    this.panelOpenState[id] = true;
  }
  openVersionHistory(shortcut: FlyCut, expanded) {
    this.dialog.openDialogComponent(VersionHistoryComponent, { self: this, shortcut }, "800px");

    if (!expanded) {
      this.panelOpenState[shortcut.id] = {};
      this.panelExpandState[shortcut.id] = {};
    }
  }

  openEmulatorPreview(shortcut: FlyCut) {
    this.dialog.openDialogComponent(EmulatorPreviewComponent, { self: this, shortcut, isMax: false }, "76vw");
  }

  tryShortcutModal(shortcut: FlyCut) {
    const dialog = this.dialog.openDialogComponent(TestFlycutComponent, { shortcut, class: "test-flycut" }, "550px");

    dialog.afterClosed().subscribe((update: boolean) => { });
  }

  openStat(id) {
    this.dialog.openDialogComponent(StatisticsComponent, { self: this, id }, "430px");
  }

  deleteShortcut(data: FlyCut, i: number) {
    this.flycutIndexToBeDeleted = i;
    this.dialog
      .confirm({
        title: "Do you want to <strong>delete</strong> this?",
        message: "Deleted items cannot be restored.",
        variant: "danger",
        confirmLabel: "Delete",
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this.delete(data);
          this.detectUpdates?.emit();
        }
      });
  }

  duplicateFlycut(data: FlyCut) {
    this.dialog.openDialogComponent(DuplicateShortcutComponent, { self: this, data }, "800px");
  }

  changeCategory(data: FlyCut) {
    this.dialog.openDialogComponent(ChangeShortcutCategoryComponent, { self: this, data }, DIALOG_WIDTHS.sm);
  }

  delete(data: FlyCut) {
    this.dataService.delete({ url: "v1/user/shortcut/" + data.id }).subscribe((response: any) => {
      this.shortcuts.splice(this.flycutIndexToBeDeleted, 1);
      this.notification.toast("FlyCut deleted successfully!");
      this.extensionService.sendUpdateMessage();
      // if (response && response.result) {
      //   this.extensionService.sendUpdateMessage();
      //   if (this.router.url != '/advanced-search-result') {
      //     // this.listService.getList(true);
      //     setTimeout(() => {
      //       this.fetchShortcuts()
      //     }, 1000);
      //   }
      //   this.detectUpdates?.emit()
      //   this.notification.toast("FlyCut deleted successfully!");
      // }
    });
  }

  get isAnyShortcutChecked(): boolean {
    return this.shortcuts.some((shortcut) => shortcut.checked);
  }

  deleteBulk(shortcuts: FlyCut[]) {
    const shortcut_ids = shortcuts.map((shortcut) => shortcut.id);

    this.dataService
      .post({ url: "v1/user/shortcut/bulkdelete", data: { shortcut_ids } }) // Sending IDs
      .subscribe((response: any) => {
        if (response && response.result) {
          this.extensionService.sendUpdateMessage();
          this.fetchShortcuts(); // Refresh the list
          this.notification.toast("Selected FlyCuts deleted successfully!");
        }
      });
  }

  deleteBulkShortcut() {
    const selectedShortcuts = this.shortcuts.filter((shortcut) => shortcut.checked);

    if (selectedShortcuts.length === 0) {
      this.notification.toast("Please select at least one FlyCut to delete.");
      return;
    }

    this.dialog
      .confirm({
        title: "Are you sure?",
        message: "After removing these messages, they cannot be restored.",
        variant: "danger",
        confirmLabel: "Delete",
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this.deleteBulk(selectedShortcuts);
          this.detectUpdates?.emit();
        }
      });
  }

  checkPlaceHolder(input: string) {
    if (this[input]) {
      this[input] = null;
      return;
    } else {
      this[input] = placeholders[input];
      return;
    }
  }

  openFlyCutShare(shortcut: FlyCut): void {
    const shortcuts = [shortcut];

    this.dialog.openDialogComponent(FlyCutShareComponent, { shortcuts: shortcuts, fromFlyPlates: false }, "859px");
  }

  updateCheckList(isChecked: boolean, index: number) {
    if (index === -1) {
      this.shortcuts = this.shortcuts.map((shortcut) => {
        // Don't check flyplate
        // if (isChecked === true && shortcut.user_defined === false) {
        //   return shortcut;
        // }

        shortcut.checked = isChecked;

        return shortcut;
      });

      return;
    }

    // Don't check flyplate
    // if (isChecked === true && this.shortcuts[index].user_defined === false) {

    //   this.dialog.openDialogComponent(
    //     FlyCutShareComponent,
    //     { shortcuts: [this.shortcuts[index]], fromFlyPlates: true  },
    //     "407px"
    //   );

    //   return;
    // }

    this.allShortCutChecked = true;
    for (const item of this.shortcuts) {
      if (!item.checked) {
        this.allShortCutChecked = false;
        break;
      }
    }
  }

  share() {
    const checked = this.shortcuts.filter((shortcut) => shortcut.checked === true);

    if (!checked || checked.length < 1) {
      this.notification.toast("You should select at least one FlyCut to share.");

      this.noShortcutChecked = true;

      setTimeout(() => {
        this.noShortcutChecked = false;
      }, 3000);

      return;
    }

    // const fromFlyPlates = checked.some(({user_defined}) => user_defined === false);

    // this.dialog.openDialogComponent(
    //   FlyCutShareComponent,
    //   { shortcuts: checked, fromFlyPlates: fromFlyPlates },
    //   fromFlyPlates ? "407px" : "859px"
    // );

    this.dialog.openDialogComponent(FlyCutShareComponent, { shortcuts: checked, fromFlyPlates: false }, "859px");
  }
}
